import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import {
  fetchTCSBalances,
  recordTCSPayment,
  fetchTCSPaymentHistory,
  fetchTCSPaymentById,
  fetchTCSTransactionBreakdown
} from "./tcsSettlementThunk";
import { ITCSSettlementSliceState } from "../../utils/tcsSettlement.types";

/**
 * State slice initial state.
 */
const initialState: ITCSSettlementSliceState = {
  balances: null,
  payments: [],
  currentPayment: null,
  transactionBreakdown: null,
  status: ApiStatus.IDLE,
  balancesStatus: ApiStatus.IDLE,
  paymentStatus: ApiStatus.IDLE,
  historyStatus: ApiStatus.IDLE,
  breakdownStatus: ApiStatus.IDLE,
  errorMessage: ''
};

/**
 * TCS Settlement State Slice.
 */
const tcsSettlementSlice = createSlice({
  name: 'tcsSettlement',
  initialState: initialState,
  reducers: {
    resetPaymentStatus: (state) => {
      state.paymentStatus = ApiStatus.IDLE;
      state.errorMessage = '';
    },
    clearBalances: (state) => {
      state.balances = null;
      state.balancesStatus = ApiStatus.IDLE;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch TCS Balances
      .addCase(fetchTCSBalances.pending, (state, action) => {
        state.balancesStatus = ApiStatus.LOADING;
      })
      .addCase(fetchTCSBalances.fulfilled, (state, action) => {
        state.balancesStatus = ApiStatus.SUCCESS;
        state.balances = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchTCSBalances.rejected, (state, action) => {
        state.balancesStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch TCS balances');
      })

      // Record TCS Payment
      .addCase(recordTCSPayment.pending, (state, action) => {
        state.paymentStatus = ApiStatus.LOADING;
      })
      .addCase(recordTCSPayment.fulfilled, (state, action) => {
        state.paymentStatus = ApiStatus.SUCCESS;
        state.currentPayment = action.payload;
        state.payments = [action.payload, ...state.payments];
        state.errorMessage = '';
        toast.success('TCS payment recorded successfully');
      })
      .addCase(recordTCSPayment.rejected, (state, action) => {
        state.paymentStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error(action.error.message || 'Failed to record TCS payment');
      })

      // Fetch TCS Payment History
      .addCase(fetchTCSPaymentHistory.pending, (state, action) => {
        state.historyStatus = ApiStatus.LOADING;
      })
      .addCase(fetchTCSPaymentHistory.fulfilled, (state, action) => {
        state.historyStatus = ApiStatus.SUCCESS;
        state.payments = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchTCSPaymentHistory.rejected, (state, action) => {
        state.historyStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch TCS payment history');
      })

      // Fetch TCS Payment By ID
      .addCase(fetchTCSPaymentById.pending, (state, action) => {
        state.status = ApiStatus.LOADING;
      })
      .addCase(fetchTCSPaymentById.fulfilled, (state, action) => {
        state.status = ApiStatus.SUCCESS;
        state.currentPayment = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchTCSPaymentById.rejected, (state, action) => {
        state.status = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch TCS payment details');
      })

      // Fetch TCS Transaction Breakdown
      .addCase(fetchTCSTransactionBreakdown.pending, (state, action) => {
        state.breakdownStatus = ApiStatus.LOADING;
      })
      .addCase(fetchTCSTransactionBreakdown.fulfilled, (state, action) => {
        state.breakdownStatus = ApiStatus.SUCCESS;
        state.transactionBreakdown = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchTCSTransactionBreakdown.rejected, (state, action) => {
        state.breakdownStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch TCS transaction breakdown');
      });
  }
});

export const { resetPaymentStatus, clearBalances } = tcsSettlementSlice.actions;
export default tcsSettlementSlice.reducer;
